// Ablauf eines Runs (R27): drei Tage flussabwärts, anlegen oder vorbeitreiben, Begegnungen, Boss, Nacht.
// Rein und deterministisch wie der Kampf (X2): gleicher Seed, gleiche Entscheidungen, gleicher Run.
// Jede Funktion ändert den übergebenen Zustand; der Zustand ist reines JSON und lässt sich speichern.
import {
  artifacts as allArtifacts,
  combatRules,
  getArtifact,
  getEncounter,
  getItem,
  itemEffects,
  itemLabel,
  itemText,
  itemUse,
  items as allItems,
  runRules,
  species as allSpecies,
  type RunRules,
  artifactOffered,
  itemOffered,
  smallTime,
  getAbility,
  getSpecies,
  fromWhom,
  systemOn,
  biomeOfDay,
} from '../../content/index.ts';
import { runBattle } from '../battle.ts';
import type { Rng } from '../rng.ts';
import type { BattleResult, BattleSetup } from '../types.ts';
import {
  combatProfile,
  displayName,
  itemSlots,
  joinWords,
  starTraits,
  maxHp,
} from './profile.ts';
import { newItem, rollLow } from '../luck.ts';
import { pickByRarity, sampleByRarity } from './rarity.ts';
import { farewellMoment, remember, takePennant } from './bonds.ts';
import { generateDay, isLoudSign, pickEvent } from './river.ts';
import { aptitude, endShift, placeOf, postsOf, startShift } from './posts.ts';
import type {
  ChronicleEntry,
  FightContext,
  Landing,
  Pending,
  RunFriend,
  RunState,
} from './types.ts';
import {
  pick,
  withRng,
  fieldSlotsOf,
  stageNow,
  isBossLanding,
  friendById,
  chronicle,
  regenMul,
  heal,
  regrowTree,
  makeFriend,
  gainXp,
  leave,
  startFight,
  spend,
  uniqueName,
  elsterParam,
  starFactor,
  freeLook,
  morningLead,
  scoutGate,
  stormClockOn,
} from './helpers.ts';
import {
  startEvent,
  returnErrands,
  foeSummary,
} from './events.ts';
export {
  available,
  chooseLevel,
  ensureLooks,
  gateStrength,
  stormClockOn,
  fieldSlotsOf,
  friendById,
  isBossLanding,
  makeFriend,
  namePool,
  optionTime,
  stageNow,
  weakestHealItem,
} from './helpers.ts';
export {
  actorChance,
  chooseOption,
  dismissReport,
  eventOptions,
  eventText,
  foeSummary,
  giveUpAttempt,
  leaveEvent,
  loreOf,
  sendOnErrand,
  startEvent,
  tryAttempt,
} from './events.ts';
export type { OptionView } from './events.ts';

// Hilfen und Freunde stehen in helpers.ts, Ereignisse und Aufträge in events.ts.

// ---------- Start ----------

/** R27: drei Freunde aus drei zufälligen Spezies, 5 Crew-Plätze (Prototyp), erster Tag */
export function newRun(seed: number, opts: { species?: string[] } = {}, rules: RunRules = runRules): RunState {
  const run: RunState = {
    version: 1,
    seed,
    rng: seed >>> 0,
    status: 'running',
    day: 1,
    landing: 0,
    step: 0,
    lead: morningLead(),
    haltsToday: 0,
    haltedAt: [],
    landings: [],
    revealed: [],
    crew: [],
    gone: [],
    crewSlots: rules.crewSlots.start,
    wood: rules.wood.start,
    inventory: [],
    artifacts: [],
    flags: {},
    usedEvents: [],
    chronicle: [],
    pending: { kind: 'river' },
    lastParty: [],
    nextId: 1,
    honorDay: 0,
    honor: null,
    recruitQueue: [],
    reports: [],
    shift: { underway: false, gemeinschaft: [] },
  };
  withRng(run, (rng) => {
    const pool = [...allSpecies.map((s) => s.id)];
    const chosen = opts.species ?? [];
    while (chosen.length < rules.startCrew) {
      const sp = pool.splice(Math.floor(rng.next() * pool.length), 1)[0]!;
      chosen.push(sp);
    }
    // R36 (Fabi, Phase Kern): Die Startcrew hat mindestens einen und höchstens zwei Frontkämpfer (Dickfell oder Raufbold)
    const classes = startClasses(rng, chosen, rules);
    chosen.forEach((sp, i) => {
      // Kann keine der Spezies vorn stehen, kommt für die letzte eine, die es kann
      if (classes[i] === null) {
        const tanky = allSpecies.filter((s) => !chosen.includes(s.id) && s.classes.some((c) => rules.startTanks.classes.includes(c.friendClass)));
        const swap = tanky[Math.floor(rng.next() * tanky.length)]!;
        chosen[i] = swap.id;
        classes[i] = swap.classes.find((c) => rules.startTanks.classes.includes(c.friendClass))!.friendClass;
      }
      run.crew.push(makeFriend(run, rng, { species: chosen[i]!, friendClass: classes[i]!, how: 'start' }));
    });
    for (const f of run.crew) remember(run, f, 'start');
    run.landings = generateDay(rng, run.usedEvents, rules, { earlyRecruit: true, day: 1, biome: biomeOfDay(1).id });
  });
  // R38: Morgens erfahrt ihr, was am Durchlass wartet
  scoutGate(run);
  // R41 (Erste Fassung): Der erste Tag führt durch den Tannenwald
  chronicle(run, 'Ablegen', `Der Sturm zieht auf. ${biomeOfDay(run.day).arrive} An Bord: ${run.crew.map(displayName).join(', ')}.${gateLine(run)}`);
  return run;
}

/**
 * R36 (Fabi, Phase Kern): Klassen der Startcrew, mindestens min und höchstens max Frontkämpfer. null heißt: Diese Spezies
 * muss gegen eine getauscht werden, die vorn stehen kann (keine der gewählten kann es).
 */
function startClasses(rng: Rng, chosen: string[], rules: RunRules): (string | null)[] {
  const tank = (c: string) => rules.startTanks.classes.includes(c);
  const out: (string | null)[] = chosen.map((sp) => pick(rng, getSpecies(sp).classes).friendClass);
  const count = () => out.filter((c) => c !== null && tank(c)).length;
  // Zu wenige: eine Spezies, die auch vorn stehen kann, wechselt dorthin
  for (let i = 0; i < out.length && count() < rules.startTanks.min; i++) {
    const t = getSpecies(chosen[i]!).classes.find((c) => tank(c.friendClass));
    if (t && !tank(out[i]!)) out[i] = t.friendClass;
  }
  if (count() < rules.startTanks.min) out[out.length - 1] = null;
  // Zu viele: von hinten wechselt, wer auch etwas anderes kann
  for (let i = out.length - 1; i >= 0 && count() > rules.startTanks.max; i--) {
    const other = getSpecies(chosen[i]!).classes.find((c) => !tank(c.friendClass));
    if (other && out[i] !== null && tank(out[i]!)) out[i] = other.friendClass;
  }
  return out;
}

// ---------- Fluss ----------

/**
 * P4: was man vom Boot aus von einer Anlegestelle sieht. B25, S12 (Fabi, 29.10.2026): Der Ausguck sieht die nächste je
 * nach Spezies: gut, was dort wartet; normal, ob dort gekämpft wird; schlecht nur das Zeichen (lookout: wer im Ausguck sitzt)
 */
export function landingView(run: RunState, index: number): { sign: string; truth: string | null; scheduled: boolean; hint: 'kampf' | 'friedlich' | null; lookout: string | null } {
  const l = run.landings[index]!;
  // I12 Fernglas: die nächste Anlegestelle; I26 Glühwürmchen im Glas (Fabi, Runde 7): jede, die man sieht
  const range = Math.max(0, ...run.artifacts.flatMap((a) => getArtifact(a).effects.map((e) => ('run' in e && e.run === 'trueSight' ? (e.value ?? 1) : 0))));
  const lookoutId = postsOf(run).ausguck;
  const lookout = lookoutId ? run.crew.find((f) => f.id === lookoutId) : undefined;
  const eye = lookout ? runRules.posts.ausguckSight[aptitude(lookout.species, 'ausguck')] : 0;
  const next = index === run.landing;
  const sight = (index >= run.landing && index < run.landing + range) || (next && eye >= 2);
  // S13 Fuchs, Schlau: Im Ausguck erkennt er einen Hinterhalt, auch wenn er sonst wenig sieht
  const sly = next && !!lookout && getSpecies(lookout.species).passive === 'schlau' && !!l.eventId && !!getEncounter(l.eventId).disguise;
  const truth = sly ? 'Hinterhalt' : run.revealed.includes(index) || sight ? (l.eventId ? getEncounter(l.eventId).name : runRules.categories.find((c) => c.id === l.category)!.name) : null;
  const fight = l.category === 'kampf' || l.category === 'befallene';
  const hint = !truth && next && eye >= 1 ? (fight ? 'kampf' : 'friedlich') : null;
  return { sign: l.sign, truth, scheduled: !!l.scheduled, hint, lookout: lookout && next && eye >= 1 ? lookout.name : null };
}

/**
 * B25 (Fabi, 29.10.2026): Was eine Strecke an Bord bewirkt, je nach Posten. Alle erholen sich auf der Fahrt (R27), je
 * weniger Leben, desto schneller (R39); der Käpt'n bekommt Erfahrung und heilt langsamer, der Heiler verarztet die in der
 * Kajüte und arbeitet dabei selbst, wer in der Kajüte liegt, ruht sich zusätzlich aus. Wie gut, hängt an der Spezies (S12)
 */
function applyStrecke(run: RunState): void {
  const r = runRules.regen;
  const P = runRules.posts;
  const mul = regenMul(run);
  const posts = postsOf(run);
  const healer = posts.heiler ? run.crew.find((f) => f.id === posts.heiler) : undefined;
  const care = healer ? P.heilerCare[aptitude(healer.species, 'heiler')] : 0;
  for (const f of run.crew) {
    if (f.away) continue;
    // I25 Mooskissen (Fabi, Runde 7): Wer sich in der Kajüte ausruht, erholt sich besser
    const moss = f.items.reduce((a, ref) => a + itemEffects(ref).reduce((b, e) => b + ('perk' in e && e.perk === 'restBoost' ? e.value : 0), 0), 0);
    const missing = 1 - Math.min(1, f.hp / maxHp(f, run));
    const travel = r.travel + ((r.travelHurt ?? r.travel) - r.travel) * missing;
    const place = placeOf(run, f.id);
    let share = travel;
    if (place === 'kaeptn') share *= P.kaeptnRegen;
    else if (place === 'heiler') share *= P.heilerRegen;
    else if (place === 'kajuete') share += P.kajuetenRest * (1 + moss) + care;
    heal(run, f, share * mul);
    regrowTree(run, f, share * mul);
    if (place === 'kaeptn') gainXp(run, f, P.kaeptnXp[aptitude(f.species, 'kaeptn')]);
  }
}

/** B25: Ablegen. Die Schicht beginnt, die Posten stehen bis zum nächsten Halt fest; was seit dem Halt gefahren ist, wirkt jetzt */
export function setOff(run: RunState): void {
  if (run.shift?.underway) return;
  const deferred = run.shift?.deferred ?? 0;
  startShift(run);
  for (let i = 0; i < deferred; i++) applyStrecke(run);
}

/** Eine Strecke weiter: Erholung, Zeitgewinn, Rückkehr von Aufträgen (R19, R27, E4) */
function advance(run: RunState, party: string[]): void {
  // B25: Ist die Schicht unterwegs, wirkt die Strecke gleich; nach einem Halt erst, wenn abgelegt wird (die Posten können
  // bis dahin noch wechseln)
  if (run.shift?.underway) applyStrecke(run);
  else run.shift = { underway: false, gemeinschaft: run.shift?.gemeinschaft ?? [], deferred: (run.shift?.deferred ?? 0) + 1 };
  run.lastParty = party;
  // E4 (Fabi, Spieltest Phase 4): Aufträge zählen Haltemöglichkeiten. Jede Anlegestelle, an der man vorbeikommt,
  // ob man anlegt oder nicht, zählt; die, an der er losgegangen ist, nicht
  for (const f of run.crew) if (f.away && f.away.since !== run.landing) f.away.haltsLeft = (f.away.haltsLeft ?? 1) - 1;
  run.landing += 1;
  run.step += 1;
  // R38 (Fabi, Phase Kern): Vorbeitreiben hält den Abstand, holt aber nichts zurück (die alte Sturmuhr holte etwas zurück)
  if (!stormClockOn()) run.lead += runRules.storm.driftGain;
  run.pending = { kind: 'river' };
  // R38: Holt euch das Unwetter ein, geht es sofort zum Durchlass, und dort wird im Unwetter gekämpft
  // Auch an der letzten Anlegestelle: Dann kommt ihr zwar ohnehin zum Durchlass, aber im Unwetter
  if (stormClockOn() && run.lead <= 1e-9 && !run.caught) caughtByStorm(run);
  returnErrands(run, isBossLanding(run));
}

/**
 * R38 (Fabi, Phase Kern): Das Unwetter hat euch eingeholt. Das Floß treibt an allen Anlegestellen vorbei zum Durchlass;
 * die Sturmwesen dort sind wieder bei voller Kraft
 */
function caughtByStorm(run: RunState): void {
  const skipped = run.landings.length - run.landing;
  run.landing = run.landings.length;
  run.caught = true;
  if (run.bossPreview?.day === run.day) run.bossPreview.strength = 1;
  const how = skipped > 0 ? 'treibt das Floß an allen übrigen Anlegestellen vorbei bis zum Durchlass' : 'erreicht mit euch den Durchlass';
  chronicle(run, 'Eingeholt', `Das Unwetter hat euch eingeholt und ${how}. Dort wird im Unwetter gekämpft, und die Sturmwesen am Durchlass sind wieder bei voller Kraft.`);
}

/** Vorbeitreiben: kostet nichts, bringt Vorsprung (R19), alle erholen sich an Bord */
export function pass(run: RunState): void {
  if (run.pending.kind !== 'river') throw new Error('Gerade kann man nicht vorbeitreiben');
  if (isBossLanding(run)) throw new Error('Am Durchlass wartet der Boss, vorbei geht es nicht');
  // B25: Wer noch nicht abgelegt hat, legt jetzt ab; vorbeitreiben gehört zur selben Schicht
  setOff(run);
  advance(run, []);
}

/**
 * Anlegen (R27): Der Landtrupp steht vor der Überfahrt fest. Anlegen kostet Zeit; was man dort tut, kostet mehr
 * (Q39, siehe spend). Bei eingeholtem Sturm ist jede Begegnung ein Kampf (R23).
 */
export function land(run: RunState, partyIds: string[]): void {
  if (run.pending.kind !== 'river') throw new Error('Gerade kann man nicht anlegen');
  const slots = fieldSlotsOf(run);
  const party = [...new Set(partyIds)];
  if (party.length === 0) throw new Error('Der Landtrupp ist leer');
  if (party.length > slots) throw new Error(`Höchstens ${slots} im Landtrupp (K4)`);
  for (const id of party) if (friendById(run, id).away) throw new Error('Wer unterwegs ist, kann nicht mit');
  // B25: Ein Halt beendet die Schicht; wer sie zusammen im Gemeinschaftsraum war, ist für den nächsten Kampf verbunden
  setOff(run);
  endShift(run);
  spend(run, runRules.storm.time.land);
  run.haltsToday += 1;
  (run.haltedAt ??= []).push(run.landing);
  for (const id of party) friendById(run, id).tally.landings += 1;
  const stage = stageNow(run);

  if (isBossLanding(run)) return startFight(run, party, 'boss');
  const l = run.landings[run.landing]!;
  // R23: Bei eingeholtem Sturm ist jede Begegnung ein Kampf; mit dem Unwetter als Uhr (R38) geht es dann gleich zum Durchlass
  if (!stormClockOn() && stage === 'eingeholt' && l.category !== 'kampf' && l.category !== 'befallene') return startFight(run, party, 'kampf');
  switch (l.category) {
    case 'kampf':
      return startFight(run, party, 'kampf');
    case 'befallene':
      return startFight(run, party, 'befallene');
    case 'haendler':
      return startTrader(run, party);
    case 'auftrag':
    {
      const pending: Pending = { kind: 'errand', eventId: l.eventId!, party, log: [], done: false };
      const intro = getEncounter(l.eventId!).text;
      if (Array.isArray(intro) && intro.length > 1) pending.textIndex = withRng(run, (rng) => Math.floor(rng.next() * intro.length));
      run.pending = pending;
      run.usedEvents.push(l.eventId!);
      return;
    }
    case 'ereignis':
      return startEvent(run, party, recruitAware(run, l));
  }
}

/**
 * R27 (Fabi, Spieltest Phase 4): Ist das Boot fast voll, kommen seltener neue Tiere des Wegs. Man sieht vorher nur
 * das Zeichen, darum darf das Ereignis erst beim Anlegen getauscht werden; feste Termine bleiben.
 */
function recruitAware(run: RunState, l: Landing): string {
  const def = getEncounter(l.eventId!);
  // Fabi (Runde 7): Hinter Lärm steckt auch nach dem Tausch etwas Lautes
  const loud = isLoudSign(l.sign);
  // R27 (Fabi, Spieltest Phase 4): Ist die Crew kaum größer als das Ufer, kommen öfter Tiere des Wegs [ZAHL?]
  // R37 (Fabi, Phase Kern): ausgeblendet, Neue kommen gleichmäßig
  if (systemOn('recruitWhenShort') && def.subject !== 'animal' && !l.scheduled && run.crew.length < fieldSlotsOf(run) + runRules.rotationSpare && run.crew.length < run.crewSlots) {
    return withRng(run, (rng) => {
      if (rng.next() >= runRules.recruitWhenShort) return def.id;
      const other = pickEvent(rng, 'ereignis', run.usedEvents, (e) => e.subject !== 'animal' || (loud && !e.noisy), biomeOfDay(run.day).id);
      if (other) l.eventId = other;
      return l.eventId!;
    });
  }
  if (def.subject !== 'animal' || l.scheduled || run.crewSlots - run.crew.length > 1) return def.id;
  return withRng(run, (rng) => {
    if (rng.next() < runRules.recruitWhenFullKeep) return def.id;
    const other = pickEvent(rng, 'ereignis', run.usedEvents, (e) => e.subject === 'animal' || (loud && !e.noisy), biomeOfDay(run.day).id);
    if (other) l.eventId = other;
    return l.eventId!;
  });
}

// ---------- Kampf ----------

/** K27: Marschrichtung als Zielpunkt, optional */
/** S10 Leibwache: ward ist die Kennung des Schützlings */
export type Placement = { id: string; x: number; y: number; march?: { x: number; y: number }; ward?: string };

/** Der Kampf, wie er gleich laufen wird: für Darstellung und Harness */
export function battleSetup(run: RunState, placements: Placement[], retreat?: string): BattleSetup {
  if (run.pending.kind !== 'fight') throw new Error('Gerade steht kein Kampf an');
  const { fight, party } = run.pending;
  for (const p of placements) if (!party.includes(p.id)) throw new Error('Nur der Landtrupp kann aufs Ufer');
  const setup: BattleSetup = {
    friends: placements.map((p) => {
      const f = friendById(run, p.id);
      // S10: der gewählte Schützling, sonst der gemerkte; steht er nicht am Ufer, wählt der Kampf den Zerbrechlichsten
      const ward = placements.findIndex((q) => q.id === (p.ward ?? f.ward) && q.id !== p.id);
      // B25: verbunden mit allen, die mit ihm eine Schicht im Gemeinschaftsraum waren und auch am Ufer stehen
      const group = (run.bonds ?? []).find((g) => g.includes(f.id));
      const mates = group ? placements.map((q, j) => (q.id !== p.id && group.includes(q.id) ? j : -1)).filter((j) => j >= 0) : [];
      const bond = mates.length ? { with: mates, bonus: bondBonus(run, group!), reach: runRules.posts.bond.reach } : null;
      return { id: f.id, species: f.species, friendClass: f.friendClass, name: f.name, profile: combatProfile(f, run), x: p.x, y: p.y, ...(p.march ? { march: p.march } : {}), ...(f.wound ? { wounded: true } : {}), ...(ward >= 0 ? { ward } : {}), ...(bond ? { bond } : {}) };
    }),
    enemies: fight.enemies,
    stormLevel: fight.stage,
    fieldSlots: fight.fieldSlots,
    xpFactor: fight.xpFactor,
    // K53 (Erste Fassung, Fabi): Große Angriffe der Sturmwesen werden am Boden angesagt, wie im Übungsufer
    telegraphs: true,
    // R38 (Fabi, Phase Kern): Sturmwesen werden durch das Unwetter nicht stärker; nur wer eingeholt am Durchlass kämpft,
    // kämpft im vollen Unwetter
    ...(stormClockOn() && !(fight.kind === 'boss' && run.caught) ? { calm: true } : {}),
  };
  // K17 (Spieltest Phase 4, Fabi): Am Durchlass gibt es keinen Rückzug, den Boss kann man nicht überspringen.
  // K43 (Fabi, Phase Kern): Ohne Rückzug im Kampf kämpft der Landtrupp überall bis zum Ende
  if (fight.kind === 'boss' || !systemOn('retreat')) setup.retreat = BOSS_RETREAT;
  // K34 (Fabi, Phase Kern): Die Schwelle wählt man nicht mehr; „normal“, mit dem Hasenfuß „ängstlich“
  else if (!systemOn('retreatChoice')) setup.retreat = retreatOptions(run)[0]!;
  else if (retreat) {
    if (!retreatOptions(run).includes(retreat)) throw new Error('Diese Rückzugsschwelle gibt es nur mit dem passenden Artefakt');
    setup.retreat = retreat;
  }
  // I26 (Fabi, Runde 7): Artefakte, die den Kampf verändern
  const effects = run.artifacts.flatMap((a) => getArtifact(a).effects).filter((e): e is Extract<typeof e, { run: string }> => 'run' in e);
  const gust = effects.filter((e) => e.run === 'gust').reduce((a, e) => a * (e.mul ?? 1), 1);
  const rod = effects.find((e) => e.run === 'lightningRod');
  if (gust !== 1 || rod) setup.mods = { ...(gust !== 1 ? { gustFactor: gust } : {}), ...(rod ? { lightningRod: { factor: rod.mul ?? 1.8, attacks: rod.value ?? 2 } } : {}) };
  return setup;
}

/** B25, S12: Wie stark ein Band ist: nach der Eignung der Verbundenen für den Gemeinschaftsraum, im Mittel */
export function bondBonus(run: RunState, group: string[]): number {
  const b = runRules.posts.bond;
  const list = group.map((id) => [...run.crew, ...run.gone].find((f) => f.id === id)).filter((f): f is RunFriend => !!f);
  return list.length ? list.reduce((a, f) => a + b[aptitude(f.species, 'gemeinschaft')], 0) / list.length : 0;
}

/** K15, I26: Welche Rückzugsschwellen es in diesem Run gibt (ängstlich nur mit Hasenfuß) */
export function retreatOptions(run: Pick<RunState, 'artifacts'>): string[] {
  // K43 (Fabi, Phase Kern): ohne Rückzug im Kampf nur „bis zum Letzten“
  if (!systemOn('retreat')) return [BOSS_RETREAT];
  const all = combatRules.retreatThresholds.filter((r) => !r.needs || run.artifacts.includes(r.needs));
  // K34 (Fabi, Phase Kern): ohne Wahl genau eine Schwelle, die vorsichtigste, die der Run hat, sonst „normal“
  if (!systemOn('retreatChoice')) {
    const extra = all.find((r) => r.needs);
    return [extra?.id ?? combatRules.retreatThresholds.find((r) => r.default)!.id];
  }
  return all.map((r) => r.id);
}

/** Rückzugsschwelle am Durchlass: kämpfen bis zum Letzten */
export const BOSS_RETREAT = 'letzter';

/** Q42 (Fabi, Spieltest Phase 4): Aufholen wie bei Pokémon, etwas schwächer. Pro Level unter dem stärksten Freund
 *  an Bord mehr XP, gedeckelt [ZAHL?] */
export function catchUpOf(run: RunState, f: RunFriend): number {
  const top = Math.max(...run.crew.map((x) => x.level), f.level);
  return Math.min(runRules.xp.catchUpMax, runRules.xp.catchUpPerLevel * (top - f.level));
}

/** F34: Vorschlag für einen Titel nach einer besonderen Tat, höchstens einmal pro Tag */
function offerHonor(run: RunState, f: RunFriend | undefined, deed: string, suggestion: string, extra: { headline?: string; about?: RunFriend } = {}): void {
  if (!f || run.honorDay === run.day || run.honor) return;
  run.honor = { friendId: f.id, deed, suggestion };
  if (extra.headline) run.honor.headline = extra.headline;
  if (extra.about) run.honor.about = { id: extra.about.id, name: extra.about.name };
  run.honorDay = run.day;
}

/** F34: Tat und Titelvorschlag mit dem aktuellen Namen des Freundes, um den es geht */
export function honorText(run: RunState): { deed: string; suggestion: string; headline: string } | null {
  const h = run.honor;
  if (!h) return null;
  const about = h.about ? ([...run.crew, ...run.gone].find((x) => x.id === h.about!.id)?.name ?? h.about.name) : '';
  return { deed: h.deed.replaceAll('{wer}', about), suggestion: h.suggestion.replaceAll('{wer}', about), headline: h.headline ?? 'Das war knapp!' };
}

/** Kampf ausfechten und das Ergebnis in den Run übernehmen (R11, F15, F21, K14, I18, E6) */
export function resolveFight(run: RunState, placements: Placement[], retreat?: string, options: { record?: boolean } = {}): BattleResult {
  if (run.pending.kind !== 'fight') throw new Error('Gerade steht kein Kampf an');
  if (placements.length === 0) throw new Error('Mindestens ein Freund muss aufs Ufer');
  const { fight, party } = run.pending;
  const setup = battleSetup(run, placements, retreat);
  // S10: Den gewählten Schützling merkt sich der Igel für die nächsten Kämpfe
  for (const p of placements) if (p.ward) friendById(run, p.id).ward = p.ward;
  const result = runBattle(setup, fight.seed, { recordFrames: !!options.record });
  // B25: Ein Band aus dem Gemeinschaftsraum gilt für den nächsten Kampf
  run.bonds = [];
  const won = result.outcome === 'win';
  const lines: string[] = [];
  const rows: NonNullable<ChronicleEntry['friends']> = [];
  let levelUps = 0;
  const leftNow: RunFriend[] = [];

  // M9: Was vertrieben wurde, zählt fürs Ende des Runs
  for (const e of result.events) {
    const u = e.type === 'down' ? result.units[e.unit] : undefined;
    if (u?.kind === 'stormling') (run.driven ??= {})[u.defId] = (run.driven[u.defId] ?? 0) + 1;
  }
  for (const s of result.friends) {
    const f = friendById(run, s.id!);
    const t = f.tally;
    t.fights += 1;
    if (won) t.wins += 1;
    t.damage += s.stats.damage;
    t.taken += s.stats.taken;
    t.healed += s.stats.healed;
    t.shielded += s.stats.shielded;
    t.kills += s.stats.kills;
    t.kniffs += s.kniffUses;
    f.hp = s.hpEnd;
    if (s.treeHpEnd !== null) f.treeHp = s.treeHpEnd;
    // F22, Q43 (Fabi): „Ich komm nach“. Wer Abschied nehmen müsste und das Versprechen trägt, kommt nach dem Kampf
    // angeschlagen zum Boot zurück, auch wenn der Kampf verloren ist. Danach bleibt es eingelöst im Platz.
    let kept = false;
    if (s.farewell) {
      const i = f.items.findIndex((id) => itemEffects(id).some((e) => 'perk' in e && e.perk === 'revive'));
      if (i >= 0) {
        const share = itemEffects(f.items[i]!).reduce((a, e) => ('perk' in e && e.perk === 'revive' ? e.value : a), 0.25);
        f.items[i] = getItem(f.items[i]!).usedAs!;
        f.hp = Math.max(1, maxHp(f, run) * share);
        kept = true;
        remember(run, f, 'promise');
        lines.push(`${f.name} kommt wie versprochen nach: angeschlagen, aber zurück an Bord.`);
      }
    }
    // I25 Staubpilz (Fabi, Runde 7): Ist er geplatzt, bleibt die leere Hülle im Platz
    if (s.sporeUsed) {
      const i = f.items.findIndex((id) => itemEffects(id).some((e) => 'perk' in e && e.perk === 'sporeCloud'));
      if (i >= 0 && getItem(f.items[i]!).usedAs) {
        f.items[i] = getItem(f.items[i]!).usedAs!;
        lines.push(`Der Staubpilz von ${f.name} ist geplatzt und hat die Gegner eingenebelt.`);
      }
    }
    // F42 (Fabi, Phase Kern): Wer fällt, kommt verwundet zurück an Bord; die Nacht heilt die Wunde
    if (s.fell && !s.farewell) {
      f.wound = { day: run.day, landing: run.landing, by: s.downBy };
      f.hp = Math.max(1, maxHp(f, run) * runRules.wound.hpShare);
      lines.push(`${f.name} ${s.downBy ? `wurde ${fromWhom(s.downBy)} erwischt` : 'ist gefallen'} und kommt verwundet zurück an Bord. Die Wunde heilt über Nacht; fällt ${f.name} vorher noch einmal, nimmt ${f.name} Abschied.`);
    }
    // K21 (Fabi, Spieltest Phase 4): Wer über den Rand geschubst wurde, wartet dort und kommt mit seinem Leben zurück
    if (s.pushedOut) lines.push(`${f.name} wurde über den Rand geschubst und wartet dort; nach dem Kampf ist ${f.name} wieder an Bord und bekommt die XP, die bis dahin verdient waren.`);
    const farewell = s.farewell && !kept;
    let xp = 0;
    let itemXp = 0;
    const itemXpFrom: string[] = [];
    const catchUp = catchUpOf(run, f);
    if (won && !s.farewell) {
      // F15 plus I6: Lernfeder und Honigwabe; Q42 (Fabi): wer hinter dem Stärksten liegt, holt auf (versteckt)
      const boost = Math.round(s.xp * f.xpBoost);
      const perKniff = Math.round((combatProfile(f, run).perks?.xpPerKniff ?? 0) * s.kniffUses);
      xp = Math.round(s.xp * (1 + catchUp)) + boost + perKniff;
      // Spieltest Phase 4 (Fabi): Was ein Item an XP bringt, steht in der Übersicht dabei
      itemXp = boost + perKniff;
      if (boost > 0) itemXpFrom.push(...new Set((f.boosts ?? []).filter((id) => getItem(id).use?.xpBoost).map((id) => itemLabel(id))));
      if (perKniff > 0) itemXpFrom.push(...f.items.filter((i) => getItem(i).effects?.some((e) => 'perk' in e && e.perk === 'xpPerKniff')).map((i) => itemLabel(i)));
    }
    f.xpBoost = 0;
    // I6: Was nur für den nächsten Kampf galt, ist jetzt verbraucht
    if (f.boosts?.length) {
      f.boosts = [];
      f.hp = Math.min(f.hp, maxHp(f, run));
    }
    // R32 (Lead-Auftrag, Fabi: mehr Belohnung pro Halt): Nach einem Sieg rastet der Landtrupp kurz am Ufer
    if (won && !farewell && runRules.regen.afterWin) heal(run, f, runRules.regen.afterWin);
    const up = gainXp(run, f, xp);
    if (up) levelUps++;
    rows.push({ id: f.id, name: f.name, damage: Math.round(s.stats.damage), taken: Math.round(s.stats.taken), healed: Math.round(s.stats.healed), xp, farewell, levelUp: up, ...(kept ? { kept } : {}), ...(catchUp > 0 && xp > 0 ? { catchUp } : {}), ...(itemXp > 0 ? { itemXp, itemXpFrom } : {}) });
    if (farewell) {
      lines.push(`${displayName(f)} nimmt Abschied.`);
      leave(run, f, 'abschied', `nimmt am Ufer Abschied (Tag ${run.day})`);
      leftNow.push(f);
    }
  }
  // F38 (Lead-Auftrag): Jeder Abschied bekommt seinen eigenen Moment; reagieren kann nur, wer noch an Bord ist
  for (const f of leftNow) farewellMoment(run, f, party);
  // F39: Wer am Ende allein das Ufer hielt, und wer am Durchlass dabei war
  if (won) {
    const standing = result.friends.filter((s) => !s.farewell && !s.pushedOut && s.hpEnd > 0);
    if (result.friends.length > 1 && standing.length === 1) {
      const f = run.crew.find((x) => x.id === standing[0]!.id);
      if (f) remember(run, f, 'lastStanding');
    }
    if (fight.kind === 'boss') for (const s of standing) {
      const f = run.crew.find((x) => x.id === s.id);
      if (f) remember(run, f, 'boss');
    }
  }
  if (run.status === 'lost') {
    chronicle(run, fightTitle(fight.kind, result.outcome), lines.join(' '), { friends: rows });
    return result;
  }

  let loot: ChronicleEntry['loot'];
  if (won) {
    // R28 (Fabi, Runde 7): Holz im Rahmen, mehr ist seltener; mit jedem Tag verschiebt sich der Rahmen nach oben [ZAHL?]
    const w = runRules.wood;
    const up = w.perDay * (run.day - 1);
    const wood = withRng(run, (rng) => rollLow(rng, [w.perWin[0]! + up, w.perWin[1]! + up]) + (fight.kind === 'boss' ? rollLow(rng, w.bossWin) : 0));
    // I6 (Fabi, Spieltest Phase 4): Nach einem Sieg liegt öfter ein Verbrauchsitem am Ufer, nach Seltenheit gewichtet
    const found = withRng(run, (rng) =>
      rng.next() < runRules.consumableDrop ? [newItem(rng, pickByRarity(rng, allItems.filter((i) => i.kind === 'consumable' && itemOffered(i))).id, starFactor(run))] : [],
    );
    const items = [...result.drops, ...found];
    loot = { wood, items };
    run.wood += wood;
    run.inventory.push(...items);
    if (items.length) lines.push(`Erbeutet: ${items.map((d) => itemLabel(d)).join(', ')}.`);
    lines.push(`+${wood} Holz.`);
    if (runRules.regen.afterWin) lines.push(`Nach dem Sieg rastet der Landtrupp kurz am Ufer und erholt sich um ${Math.round(runRules.regen.afterWin * 100)} %.`);
    // F34: knapp gewonnen
    const share = result.friends.reduce((a, s) => a + (s.farewell ? 0 : s.hpEnd), 0) / result.friends.reduce((a, s) => a + s.maxHp, 0);
    const best = [...result.friends].filter((s) => !s.farewell).sort((a, b) => b.stats.damage + b.stats.taken + b.stats.healed - (a.stats.damage + a.stats.taken + a.stats.healed))[0];
    if (share < 0.3 && best) offerHonor(run, run.crew.find((f) => f.id === best.id), 'hat einen Kampf gerade noch gewonnen', 'Letzter am Ufer');
    // E34 Hagelchen-Nest (Fabi, Runde 7): Wer das Nest zerstört, hat den Rest des Tages schwächere Hagelchen
    if (fight.onWin) {
      run.flags[fight.onWin] = true;
      if (fight.onWin === 'tag:hagelnest-zerstoert') lines.push('Das Nest ist zerstört, die Hagelchen sind für heute geschwächt.');
    }
    // R38 (Fabi, Phase Kern): Jeder gewonnene Kampf unterwegs schwächt den Durchlass sichtbar, bis zu einer Grenze, damit
    // er nicht zu leicht wird. Platzhalter bis zum Lore-Lead: Die Sturmwesen vom Ufer sammeln sich abends am Durchlass
    const gate = run.bossPreview;
    if (stormClockOn() && fight.kind !== 'boss' && gate?.day === run.day && !run.caught) {
      const c = runRules.storm.clock;
      const before = gate.strength ?? 1;
      gate.strength = Math.max(c.floor, before - c.weakenPerWin);
      if (gate.strength < before - 1e-9) lines.push(`Diese Sturmwesen fehlen heute Abend am Durchlass: Er hat nur noch ${Math.round(gate.strength * 100)} % seiner Kraft.`);
      else lines.push('Der Durchlass ist schon so schwach, wie er heute werden kann.');
    }
  } else {
    lines.push(result.outcome === 'retreat' ? 'Rückzug ans Boot, aus dieser Begegnung gibt es nichts.' : 'Niederlage, aus dieser Begegnung gibt es nichts.');
  }
  if (levelUps) lines.push(`${levelUps === 1 ? 'Ein Level-Up' : `${levelUps} Level-Ups`}.`);
  chronicle(run, fightTitle(fight.kind, result.outcome), lines.join(' '), loot ? { friends: rows, loot } : { friends: rows });

  // E6, E27: Befreite und Gerettete wollen mit
  let candidate: RunFriend | undefined;
  if (won && fight.infested && result.freed.length) {
    candidate = fight.infested;
    candidate.hp = Math.max(1, maxHp(candidate, run) * 0.25);
    const liberator = [...result.friends].filter((s) => !s.farewell).sort((a, b) => b.stats.damage - a.stats.damage)[0];
    offerHonor(run, run.crew.find((f) => f.id === liberator?.id), 'hat {wer} befreit', '{wer}-Retter', { headline: 'Befreit!', about: candidate });
  } else if (won && fight.subject) {
    candidate = fight.subject;
    candidate.hp = Math.max(1, maxHp(candidate, run) * 0.6);
  }

  if (fight.kind === 'boss') {
    if (!won) {
      // K17: Der Boss ist beim nächsten Versuch wieder heil, ein neuer Versuch kostet wieder Zeit
      run.pending = { kind: 'river' };
      if (stormClockOn()) retreatBeforeGate(run);
      return result;
    }
    run.crewSlots += runRules.crewSlots.perBoss;
    chronicle(run, 'Durchlass offen', `Der Boss ist vertrieben. Ein Platz mehr an Bord (jetzt ${run.crewSlots}).`);
    if (run.day >= runRules.days) {
      run.status = 'won';
      run.pending = { kind: 'end' };
      chronicle(run, 'Ziel erreicht', `Nach ${run.day} Tagen ist die Reise geschafft. An Bord: ${run.crew.map(displayName).join(', ')}.`);
      return result;
    }
    // I22 (Fabi): Welche Artefakte zur Wahl stehen, hängt von ihrer Seltenheit ab
    const left = allArtifacts.filter((a) => !run.artifacts.includes(a.id) && artifactOffered(a));
    // S8 (Fabi, Runde 8): Mit der Elster an Bord liegt ein Artefakt mehr zur Wahl
    run.pending = left.length ? { kind: 'bossReward', choices: withRng(run, (rng) => sampleByRarity(rng, left, 2 + elsterParam(run, 'extraArtifactChoices', 0)).map((a) => a.id)) } : { kind: 'night' };
    return result;
  }
  if (candidate) {
    // F39: Befreit von einer Sturmklette
    remember(run, candidate, 'befreit');
    run.pending = { kind: 'recruit', candidate: uniqueName(run, candidate), log: [], then: null, party };
    return result;
  }
  advance(run, party);
  return result;
}

/** R38: Was morgens über den Durchlass bekannt ist, als Satz für die Chronik */
function gateLine(run: RunState): string {
  return stormClockOn() && run.bossPreview?.day === run.day ? ` Am Durchlass warten heute: ${foeSummary(run.bossPreview.enemies)}.` : '';
}

/**
 * R38 (Phase Kern): Nach einer Niederlage am Durchlass zieht sich die Truppe aufs Floß zurück und rastet im Schutz des
 * Ufers, bevor sie es noch einmal versucht. Vorher ging es ohne Rast gleich wieder los, und mit Verwundeten führte das
 * fast immer zu Abschieden. Die Rast kostet Zeit; holt euch dabei das Unwetter ein, sind die Sturmwesen am Durchlass
 * wieder bei voller Kraft [ZAHL?]
 */
function retreatBeforeGate(run: RunState): void {
  const c = runRules.storm.clock;
  for (const f of run.crew) if (!f.away) heal(run, f, c.retryRest * regenMul(run));
  spend(run, c.retryTime);
  let line = `Ihr zieht euch aufs Floß zurück und rastet im Schutz des Ufers. Alle erholen sich um ${Math.round(c.retryRest * 100)} %; wer verwundet ist, bleibt es bis zur Nacht.`;
  if (run.lead <= 1e-9 && !run.caught) {
    run.caught = true;
    if (run.bossPreview?.day === run.day) run.bossPreview.strength = 1;
    line += ' Während der Rast hat euch das Unwetter eingeholt; die Sturmwesen am Durchlass sind wieder bei voller Kraft.';
  } else line += ' Das Unwetter ist dabei näher gekommen.';
  chronicle(run, 'Rast vor dem Durchlass', line);
}

function fightTitle(kind: FightContext['kind'], outcome: BattleResult['outcome']): string {
  const what = { kampf: 'Kampf', befallene: 'Befallene', hilferuf: 'Hilferuf', hinterhalt: 'Hinterhalt', boss: 'Boss' }[kind];
  const how = { win: 'gewonnen', retreat: 'Rückzug', loss: 'verloren' }[outcome];
  return `${what}: ${how}`;
}

// ---------- Aufnehmen ----------

/** B17, P3: Neuer Freund. Ist die Crew voll, muss jemand gehen, oder er zieht weiter. */
export function acceptRecruit(run: RunState, releaseId?: string): void {
  if (run.pending.kind !== 'recruit') throw new Error('Gerade will niemand mit');
  const { candidate, then, party } = run.pending;
  if (run.crew.length >= run.crewSlots) {
    if (!releaseId) throw new Error('Die Crew ist voll: Wer soll gehen?');
    const f = friendById(run, releaseId);
    // I29 (Runde 8): Wer Platz macht, lässt seine Items für die anderen da
    const left = f.items.splice(0);
    run.inventory.push(...left);
    leave(run, f, 'zurueckgelassen', `bleibt am Ufer zurück, damit ${candidate.name} Platz hat (Tag ${run.day})`);
    chronicle(run, 'Abschied', `${displayName(f)} bleibt am Ufer zurück und macht Platz.${left.length ? ` ${f.name} lässt ${left.map(itemLabel).join(' und ')} für euch da.` : ''}`);
  }
  candidate.origin = { ...candidate.origin, day: run.day, landing: Math.min(run.landing, run.landings.length) + 1 };
  // T37: Er bekommt ein Aussehen, das in der Crew noch frei ist
  candidate.look = freeLook(run, candidate.species);
  run.crew.push(candidate);
  // F37 (Fabi, Runde 7): Was den Neuen besonders macht, gehört zu seiner Geschichte
  const stars = starTraits(candidate);
  chronicle(run, 'Neuer Freund', `${displayName(candidate)} (Level ${candidate.level}) kommt an Bord.${stars.length ? ` ${candidate.name} ist besonders ${joinWords(stars)}.` : ''}`);
  afterRecruit(run, then, party);
}

/** E6: Wer nicht mitkommt, lässt etwas da */
export function declineRecruit(run: RunState): void {
  if (run.pending.kind !== 'recruit') throw new Error('Gerade will niemand mit');
  const { candidate, then, party } = run.pending;
  run.wood += 2;
  chronicle(run, 'Weitergezogen', `${displayName(candidate)} zieht allein weiter und lässt 2 Holz da.`);
  afterRecruit(run, then, party);
}

function afterRecruit(run: RunState, then: Pending | null, party: string[]): void {
  if (then) {
    run.pending = then;
    return;
  }
  advance(run, party);
}

// ---------- Händler ----------

function startTrader(run: RunState, party: string[]): void {
  // E25: Wer bestohlen hat, zahlt beim nächsten Händler mehr
  const markup = run.flags['haendler-teurer'] ? 1.5 : 1;
  if (run.flags['haendler-teurer']) delete run.flags['haendler-teurer'];
  // S13 Fuchs, Schlau (Erste Fassung): Ist er im Landtrupp, handelt er den Preis herunter
  const fox = party.map((id) => run.crew.find((f) => f.id === id)).find((f) => f && getSpecies(f.species).passive === 'schlau');
  const sly = fox ? 1 - getAbility('schlau').params.discount! : 1;
  const offers = withRng(run, (rng) => {
    // I22 (Fabi, Spieltest Phase 4): nach Seltenheit gewichtet, dazu öfter Verbrauchsitems (I6)
    const equip = sampleByRarity(rng, allItems.filter((i) => i.kind === 'equip' && itemOffered(i)), 3);
    const cons = sampleByRarity(rng, allItems.filter((i) => i.kind === 'consumable' && itemOffered(i)), 2);
    return [...equip, ...cons].map((i) => ({ item: newItem(rng, i.id, starFactor(run)), price: Math.max(1, Math.ceil(i.price * markup * sly - 1e-9)) }));
  });
  const log = markup > 1 ? ['Der Händler ist mit dem Bruder des Siebenschläfers befreundet und hat schon alles gehört. Heute ist bei ihm alles teurer.'] : [];
  if (fox) log.push(`${fox.name} handelt den Preis herunter, und der Händler lacht: „Na gut, weil du so nett fragst.“`);
  run.pending = { kind: 'trader', party, offers, log };
}

export function buy(run: RunState, index: number): void {
  if (run.pending.kind !== 'trader') throw new Error('Kein Händler da');
  const offer = run.pending.offers[index];
  if (!offer) throw new Error('Gibt es nicht');
  if (run.wood < offer.price) throw new Error('Zu wenig Holz');
  run.wood -= offer.price;
  spend(run, smallTime('buy'));
  run.inventory.push(offer.item);
  run.pending.offers.splice(index, 1);
  run.pending.log.push(`Gekauft: ${itemLabel(offer.item)} für ${offer.price} Holz.`);
}

/** Händler, Ereignis oder Auftrag abschließen und zurück zum Boot */
export function leaveShore(run: RunState): void {
  const p = run.pending;
  if (p.kind === 'trader') {
    chronicle(run, 'Händler', p.log.length ? p.log.join(' ') : 'Nur geschaut.');
    return advance(run, p.party);
  }
  if ((p.kind === 'event' || p.kind === 'errand') && p.done) {
    return advance(run, p.party);
  }
  throw new Error('Hier geht es noch nicht zurück');
}

// ---------- Boss, Nacht ----------

/** I13: Artefakt nach dem Boss */
export function chooseArtifact(run: RunState, id: string | null): void {
  if (run.pending.kind !== 'bossReward') throw new Error('Gerade gibt es kein Artefakt');
  if (id !== null) {
    if (!run.pending.choices.includes(id)) throw new Error('Steht nicht zur Wahl');
    run.artifacts.push(id);
    chronicle(run, 'Artefakt', `${getArtifact(id).name}: ${getArtifact(id).text}`);
  }
  run.pending = { kind: 'night' };
}

/** R20: Nachts tuckert das Boot ins nächste Stück Fluss; alle erholen sich, das Feld wächst (K4) */
export function startNextDay(run: RunState): void {
  if (run.pending.kind !== 'night') throw new Error('Es ist nicht Nacht');
  // F42 (Fabi, Phase Kern): Über Nacht heilen die Wunden
  const healed = run.crew.filter((f) => f.wound).map((f) => f.name);
  for (const f of run.crew) {
    f.away = null;
    f.wound = null;
    heal(run, f, runRules.regen.night);
    regrowTree(run, f, runRules.regen.night);
  }
  if (healed.length) chronicle(run, 'Wunden verheilt', `Über Nacht sind die Wunden von ${joinWords(healed)} verheilt.`);
  run.day += 1;
  run.landing = 0;
  run.step += 1;
  run.lead = morningLead();
  run.haltsToday = 0;
  run.haltedAt = [];
  run.revealed = [];
  run.lastParty = [];
  // B25: Am Morgen beginnt eine neue Schicht; zuteilen geht, bis abgelegt wird
  run.shift = { underway: false, gemeinschaft: [] };
  // Runde 7: Merkzeichen, die nur für einen Tag gelten (etwa das Hagelchen-Nest), verfallen über Nacht
  for (const k of Object.keys(run.flags)) if (k.startsWith('tag:')) delete run.flags[k];
  // R27 (Fabi, Spieltest Phase 4): Man soll fast nie alle mit an Land nehmen können, damit man rotieren muss.
  // R37 (Fabi, Phase Kern): Neue aus Not sind ausgeblendet; die frühe Bewerbung am ersten Tag bleibt
  const short = systemOn('recruitWhenShort') && run.crew.length < fieldSlotsOf(run) + runRules.rotationSpare;
  withRng(run, (rng) => {
    run.landings = generateDay(rng, run.usedEvents, runRules, { earlyRecruit: short, day: run.day, biome: biomeOfDay(run.day).id });
  });
  // R38: Morgens erfahrt ihr, was am Durchlass wartet
  scoutGate(run);
  run.pending = { kind: 'river' };
  // R41 (Erste Fassung): Jeder Tag hat sein Biom
  const biome = biomeOfDay(run.day);
  chronicle(run, `Tag ${run.day}: ${biome.name}`, `Ein neuer Morgen. ${biome.arrive} Am Ufer ist Platz für ${fieldSlotsOf(run)} Freunde.${gateLine(run)}`);
}

// ---------- Items (I1, I2) ----------

export function equipItem(run: RunState, friendId: string, itemId: string): void {
  const f = friendById(run, friendId);
  const def = getItem(itemId);
  const i = run.inventory.indexOf(itemId);
  if (i < 0) throw new Error('Nicht im Inventar');
  if (def.kind !== 'equip') throw new Error('Das wird benutzt, nicht angelegt');
  if (f.items.length >= itemSlots(run)) throw new Error('Alle Itemplätze belegt');
  const before = maxHp(f, run);
  run.inventory.splice(i, 1);
  f.items.push(itemId);
  f.hp += maxHp(f, run) - before;
}

export function useItem(run: RunState, friendId: string, itemId: string): void {
  const f = friendById(run, friendId);
  const def = getItem(itemId);
  const i = run.inventory.indexOf(itemId);
  if (i < 0) throw new Error('Nicht im Inventar');
  if (def.kind !== 'consumable' || !def.use) throw new Error('Das wird angelegt, nicht benutzt');
  run.inventory.splice(i, 1);
  const use = itemUse(itemId);
  if (use.heal) heal(run, f, use.heal);
  if (use.xpBoost) f.xpBoost += use.xpBoost;
  // I6 (Fabi, Spieltest Phase 4): stärkt für den nächsten Kampf
  const before = maxHp(f, run);
  if (def.use.next?.length || def.use.xpBoost) (f.boosts ??= []).push(itemId);
  f.hp += maxHp(f, run) - before;
  chronicle(run, def.name, `${f.name} bekommt ${itemLabel(itemId)}. ${itemText(itemId)}`);
}

// ---------- Namen und Titel (F33, F34) ----------

export function renameFriend(run: RunState, friendId: string, name: string): void {
  const n = name.trim().slice(0, 24);
  if (n) friendById(run, friendId).name = n;
}

/** F38 (Lead-Auftrag): Nach dem Abschied hängt ihr selbst einen Wimpel für den Freund ans Floß */
export function hangPennant(run: RunState, friendId: string): void {
  const line = takePennant(run, friendId);
  if (line) chronicle(run, 'Wimpel', line);
}

export function answerHonor(run: RunState, title: string | null): void {
  if (!run.honor) return;
  const t = title?.trim().slice(0, 32);
  const f = run.crew.find((x) => x.id === run.honor!.friendId);
  if (f && t) {
    f.titles.push(t);
    remember(run, f, 'title', { titel: t });
    chronicle(run, 'Titel', `${f.name} heißt jetzt auch „${t}“: ${honorText(run)!.deed}.`);
  }
  run.honor = null;
}

/**
 * R20: Tageslicht. Jede Strecke und jeder Halt lässt den Tag weiterlaufen: Morgendunst zu Beginn,
 * Dämmerung um den Boss bei normalem Spiel, Nacht ab dem 13. Halt.
 */
export function daylight(run: Pick<RunState, 'landing' | 'haltsToday'>): { id: string; name: string; share: number } {
  const t = run.landing + run.haltsToday;
  const share = Math.min(1, t / 28);
  if (run.haltsToday >= 13) return { id: 'nacht', name: 'Nacht', share };
  if (t < 6) return { id: 'morgen', name: 'Morgendunst', share };
  if (t < 12) return { id: 'vormittag', name: 'Vormittag', share };
  if (t < 17) return { id: 'mittag', name: 'Mittag', share };
  if (t < 22) return { id: 'nachmittag', name: 'Nachmittag', share };
  if (t < 25) return { id: 'abend', name: 'Abend', share };
  return { id: 'daemmerung', name: 'Dämmerung', share };
}
